import { Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as BookmarkIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as DataSourceIcon } from "../../assets/icons/catalog.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/clock-refresh.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as InfoOutline } from "../../assets/icons/info_outline.svg";
import { ReactComponent as ListIcon } from "../../assets/icons/list.svg";
import { ReactComponent as MessageSolvedSvg } from "../../assets/icons/message_solved.svg";
import { ReactComponent as SettingsGearIcon } from "../../assets/icons/settings.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import { ReactComponent as UsersIcon } from "../../assets/icons/users.svg";

const mainColor = 'rgba(29, 42, 65, 1)';

const AdminMenuWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const AdminButton = styled.button<{ $active: boolean; $isOpen: boolean }>`
    display: flex;
    gap: 17px;
    align-items: center;
    justify-content: ${({$isOpen}) => ($isOpen ? "space-between" : "center")};
    background: transparent;
    border: 1px solid transparent;
    border-radius: 14px;
    padding: ${({$isOpen}) => ($isOpen ? "5px" : "10px 10px")};
    cursor: pointer;
    font-size: ${({$isOpen}) => ($isOpen ? "15px" : "20px")};
    font-weight: 500;
    transition: background 0.3s;
    pointer-events: auto;
    width: ${({$isOpen}) => ($isOpen ? "100%" : "50px")};
    margin-top: 8px;

    &:hover {
        background: #efefef;
    }

    svg:first-of-type {
        color: ${mainColor};
        width: 22px;
        height: 22px;
    }
`;

const Arrow = styled.div<{ $isExpanded: boolean; $isOpen: boolean }>`
    width: 7px;
    height: 7px;
    border-left: 2px solid ${mainColor};
    border-bottom: 2px solid ${mainColor};
    transform: ${({$isExpanded}) => ($isExpanded ? "rotate(135deg)" : "rotate(-45deg)")};
    transition: transform 0.3s ease-in-out;
    ${({$isOpen}) => !$isOpen && "display: none;"};
    margin-left: auto;
    margin-right: 7px;
`;

const DropdownMenu = styled.div<{ $open: boolean; $isOpen: boolean }>`
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid transparent;
    gap: 1px;
    border-radius: 8px;
    margin-top: 8px;
    overflow: hidden;
    max-height: ${({$open}) => ($open ? "305px" : "0")};
    opacity: ${({$open}) => ($open ? "1" : "0")};
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    ${({$isOpen}) => !$isOpen && "align-items: center; width: 50px;"}
    padding-left: ${({$isOpen}) => $isOpen ? "12px" : "0"};
`;

const DropdownItem = styled.button<{ $active: boolean; $isOpen: boolean }>`
    display: flex;
    align-items: center;
    gap: ${({$isOpen}) => ($isOpen ? "12px" : "0")};
    padding: ${({$isOpen}) => ($isOpen ? "5px 12px" : "10px")};
    font-size: 14px;
    border-radius: 32px;
    border: none;
    cursor: pointer;
    pointer-events: ${({$active}) => ($active ? "none" : "auto")};
    background: ${({$active}) => ($active ? mainColor : "transparent")};
    color: ${({$active}) => ($active ? "white" : "inherit")};
    transition: background 0.3s, padding 0.3s, gap 0.3s, color 0.3s;
    justify-content: ${({$isOpen}) => ($isOpen ? "flex-start" : "center")};
    width: ${({$isOpen}) => ($isOpen ? "100%" : "50px")};

    &:hover {
        background: #efefef;
    }

    svg {
        width: 20px;
        height: 20px;
        color: ${({$active}) => ($active ? "white" : mainColor)};
    }

    ${({$isOpen}) => !$isOpen && "span { display: none; }"}
`;

const AdminDropdown: React.FC<{ isOpen: boolean }> = ({isOpen}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const menuItems = [
        {path: "/admin/user/list", label: t('sidebar.users'), icon: <UserIcon/>, id: "users"},
        {path: "/admin/group/list", label: t('sidebar.groups'), icon: <UsersIcon/>, id: "groups"},
        {
            path: "/admin/data-sources/list",
            label: t('sidebar.dataSourcesAdmin'),
            icon: <DataSourceIcon/>,
            id: "data-sources"
        },
        {path: "/admin/scheduler", label: t('sidebar.scheduler'), icon: <SettingsIcon/>, id: "scheduler"},
        {
            path: "/admin/reported-issues",
            label: t('sidebar.reportedIssues'),
            icon: <MessageSolvedSvg/>,
            id: "reported-issues"
        },
        {path: "/admin/prompts", label: t('sidebar.prompts'), icon: <EditIcon/>, id: "prompts"},
        {path: "/admin/logs", label: t('sidebar.logs'), icon: <ListIcon/>, id: "logs"},
        {
            path: "/admin/configuration",
            label: t('sidebar.configuration'),
            icon: <SettingsGearIcon/>,
            id: "configuration"
        },
        {path: "/admin/app-info", label: t('sidebar.appInfo'), icon: <InfoOutline/>, id: "info"},
    ];

    const isActiveItem = (itemPath: string) => location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);
    const isAnyActive = menuItems.some((item) => isActiveItem(item.path));

    useEffect(() => {
        if (isAnyActive) {
            setIsExpanded(true);
        }
    }, [isAnyActive, location.pathname]);

    return (
        <AdminMenuWrapper>
            <Tooltip title={isExpanded ? t('sidebar.collapseAdminPanel') : t('sidebar.expandAdminPanel')}>
                <AdminButton
                    onClick={() => setIsExpanded((prev) => !prev)}
                    $active={isAnyActive}
                    $isOpen={isOpen}
                >
                    {isOpen && <BookmarkIcon/>}
                    <span>{isOpen ? t('sidebar.adminPanel') : "A"}</span>
                    {isOpen && <Arrow $isExpanded={isExpanded} $isOpen={isOpen}/>}
                </AdminButton>
            </Tooltip>
            {isExpanded && <DropdownMenu $open={isExpanded} $isOpen={isOpen}>
                {menuItems.map(({path, label, icon, id}) => (
                    <DropdownItem
                        key={id}
                        onClick={() => navigate(path)}
                        $active={isActiveItem(path)}
                        $isOpen={isOpen}
                    >
                        {icon}
                        {isOpen && <span>{label}</span>}
                    </DropdownItem>
                ))}
            </DropdownMenu>}
        </AdminMenuWrapper>
    );
};

export default AdminDropdown;
